import React, { useState } from 'react';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${url}`;
};

function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-surface-container-high">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

function AnimalDetails({ a }) {
  const images = (a.images && a.images.length > 0) ? a.images : (a.image_url ? [a.image_url] : []);
  return (
    <div className="p-4 sm:p-5 bg-surface-container-low/60 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-4">
      {images.length > 0 && (
        <div className="flex gap-2 overflow-x-auto sm:max-w-xs">
          {images.map((url, i) => (
            <img key={i} src={getImageUrl(url)} alt={`${a.name} ${i + 1}`}
              className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
        <div><p className="text-secondary">Breed</p><p className="font-semibold">{a.breed || '—'}</p></div>
        <div><p className="text-secondary">Gender</p><p className="font-semibold">{a.gender || '—'}</p></div>
        <div><p className="text-secondary">Age</p><p className="font-semibold">{a.age_months ? `${a.age_months} months` : '—'}</p></div>
        <div><p className="text-secondary">State</p><p className="font-semibold">{a.location_state || '—'}</p></div>
        <div><p className="text-secondary">Listed on</p><p className="font-semibold">{a.created_at ? new Date(a.created_at).toLocaleDateString('en-IN') : '—'}</p></div>
        <div><p className="text-secondary">Sold</p><p className="font-semibold">{(a.initial_quantity || 0) - (a.quantity || 0)} of {a.initial_quantity || 0}</p></div>
        {a.description && (
          <div className="col-span-2 sm:col-span-3">
            <p className="text-secondary">Description</p>
            <p className="font-medium text-secondary/90 mt-0.5">{a.description}</p>
          </div>
        )}
        {a.status === 'pending' && a.admin_note && (
          <div className="col-span-2 sm:col-span-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-start gap-2">
            <span className="material-symbols-outlined text-amber-600 text-[16px] mt-0.5">edit_note</span>
            <div>
              <p className="text-amber-700 font-black uppercase text-[10px] tracking-wide">Admin requested changes</p>
              <p className="text-amber-700 font-medium mt-0.5">{a.admin_note}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ListingsPanel({ animals, loading, onAddAnimal, onEdit, onDelete, highlightAnimalId, onHighlightDone }) {
  const [view, setView] = useState('table'); // 'table' | 'grid'
  const [perPage, setPerPage] = useState(5);
  const [expandedId, setExpandedId] = useState(null);
  const pagination = usePagination(animals, perPage);
  const toggleExpand = (id) => setExpandedId(curr => curr === id ? null : id);
  const highlightRef = React.useRef(null);

  React.useEffect(() => {
    if (highlightAnimalId) {
      setExpandedId(highlightAnimalId);
      const timer = setTimeout(() => {
        if (highlightRef.current) {
          highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      const clearTimer = setTimeout(() => onHighlightDone && onHighlightDone(), 4000);
      return () => { clearTimeout(timer); clearTimeout(clearTimer); };
    }
  }, [highlightAnimalId, onHighlightDone]);

  if (loading) return (
    <div className="py-16 flex justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-surface-container-high flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <h3 className="font-bold text-base sm:text-lg">
          My Listings <span className="text-secondary font-normal text-sm">({animals.length} total)</span>
        </h3>
        <div className="flex items-center gap-2">
          {/* Toggle */}
          <div className="flex items-center bg-surface-container-low rounded-lg p-0.5 gap-1">
            <button
  onClick={() => setView('table')}
  className={`flex items-center justify-center p-1.5 rounded-md transition-all ${view === 'table' ? 'bg-white shadow text-primary' : 'text-secondary hover:text-primary'}`}
>
  <span className="material-symbols-outlined text-[18px]">table_rows</span>
</button>
<button
  onClick={() => setView('grid')}
  className={`flex items-center justify-center p-1.5 rounded-md transition-all ${view === 'grid' ? 'bg-white shadow text-primary' : 'text-secondary hover:text-primary'}`}
>
  <span className="material-symbols-outlined text-[18px]">grid_view</span>
</button>
          </div>
          <button onClick={onAddAnimal}
            className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-primary text-white font-bold text-xs sm:text-sm hover:opacity-90 transition-all">
            <span className="material-symbols-outlined text-[18px]">add</span>
            <span className="hidden xs:inline sm:inline">Add Animal</span>
          </button>
        </div>
      </div>

      {animals.length === 0 ? (
        <div className="py-16 text-center text-secondary">
          <span className="material-symbols-outlined text-4xl text-outline-variant">pets</span>
          <p className="mt-2 font-semibold">No listings yet</p>
          <p className="text-xs mt-1 text-outline">Add your first animal to get started</p>
        </div>

      ) : view === 'grid' ? (
        /* ── GRID VIEW ── */
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {pagination.slice.map(a => (
            <div key={a.id} ref={a.id === highlightAnimalId ? highlightRef : null}
              className={`rounded-2xl border overflow-hidden hover:shadow-md transition-shadow ${
                a.id === highlightAnimalId ? 'border-primary ring-2 ring-primary/40' : 'border-surface-container'
              }`}>
              {/* Image */}
              <div className="relative h-44 bg-surface-container-low">
                {a.image_url
                    ? <img 
                        src={getImageUrl(a.image_url)} 
                        alt={a.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log('Image failed to load:', a.image_url, '→', getImageUrl(a.image_url));
                          e.target.style.display = 'none';
                        }}
                      />
                    : <div className="w-full h-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-5xl text-outline-variant">pets</span>
                      </div>
                  }
                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  a.status === 'active'  ? 'bg-emerald-100 text-emerald-700' :
                  a.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                  a.status === 'sold'    ? 'bg-blue-100 text-blue-700' :
                                           'bg-gray-100 text-gray-600'
                }`}>{a.status}</span>
              </div>
              {/* Info */}
              <div className="p-3">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-bold text-sm">{a.name}</span>
                  <span className="text-sm font-bold text-primary">₹{Number(a.price_inr).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex gap-3 text-xs text-secondary mt-1">
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">weight</span>
                    {a.weight_kg ? `${a.weight_kg}kg` : '—'}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">location_on</span>
                    {a.location_city || '—'}
                  </span>
                </div>
                <div className="mt-2 text-xs text-outline bg-surface-container-low rounded-md px-2 py-1 inline-block">
                  {a.category || '—'}
                </div>

                <div className="mt-1 text-[11px] text-secondary">
                  Stock: {a.quantity}/{a.initial_quantity} · Sold: {(a.initial_quantity || 0) - (a.quantity || 0)}
                </div>

                {a.status === 'pending' && a.admin_note && (
                  <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                    <span className="material-symbols-outlined text-amber-600 text-[14px] mt-0.5">edit_note</span>
                    <div>
                      <p className="text-[10px] font-black text-amber-700 uppercase tracking-wide">Changes requested</p>
                      <p className="text-[11px] text-amber-700 mt-0.5">{a.admin_note}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-1.5 mt-2">
                  <button onClick={() => toggleExpand(a.id)}
                    className="flex-1 py-1 rounded-lg text-[11px] font-bold bg-surface-container text-secondary hover:bg-surface-container-high transition-all">
                    {expandedId === a.id ? 'Hide Details' : 'Details'}
                  </button>
                  {(a.status === 'pending' || a.status === 'draft') && (
                    <>
                      <button onClick={() => onEdit(a)}
                        className="flex-1 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all">
                        Edit
                      </button>
                      <button onClick={() => onDelete(a.id)}
                        className="flex-1 py-1 rounded-lg text-[11px] font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-all">
                        Delete
                      </button>
                    </>
                  )}
                  {a.status === 'active' && a.quantity === a.initial_quantity && (
                    <button onClick={() => onDelete(a.id)}
                      className="flex-1 py-1 rounded-lg text-[11px] font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-all">
                      Delete
                    </button>
                  )}
                </div>
                {expandedId === a.id && (
                  <div className="-mx-3 -mb-3 mt-2 border-t border-surface-container">
                    <AnimalDetails a={a} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-surface-container">
            {pagination.slice.map(a => (
              <div key={a.id} ref={a.id === highlightAnimalId ? highlightRef : null}
                className={a.id === highlightAnimalId ? 'bg-primary/5 ring-2 ring-inset ring-primary/40' : ''}>
                <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => toggleExpand(a.id)}>
                  {a.image_url
                    ? <img src={a.image_url} alt={a.name}
                        className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                    : <div className="w-14 h-14 rounded-xl bg-surface-container flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-outline">pets</span>
                      </div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{a.name}</p>
                    <p className="text-xs text-secondary">{a.category} · {a.location_city || '—'}</p>
                    <p className="text-xs text-secondary mt-0.5">Stock: {a.quantity}/{a.initial_quantity}</p>
                  </div>
                  <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                    <p className="text-sm font-bold text-primary">₹{Number(a.price_inr).toLocaleString('en-IN')}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      a.status === 'active'  ? 'bg-emerald-100 text-emerald-700' :
                      a.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      a.status === 'sold'    ? 'bg-blue-100 text-blue-700' :
                                               'bg-gray-100 text-gray-600'
                    }`}>{a.status}</span>
                    {(a.status === 'pending' || a.status === 'draft') && (
                      <div className="flex gap-1 mt-1">
                        <button onClick={(e) => { e.stopPropagation(); onEdit(a); }}
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600">
                          Edit
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-600">
                          Delete
                        </button>
                      </div>
                    )}
                    {a.status === 'active' && a.quantity === a.initial_quantity && (
                      <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-600 mt-1">
                        Delete
                      </button>
                    )}
                  </div>
                  <span className="material-symbols-outlined text-outline flex-shrink-0">
                    {expandedId === a.id ? 'expand_less' : 'expand_more'}
                  </span>
                </div>
                {a.status === 'pending' && a.admin_note && (
                  <div className="mx-4 mb-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                    <span className="material-symbols-outlined text-amber-600 text-[14px] mt-0.5">edit_note</span>
                    <p className="text-[11px] text-amber-700">{a.admin_note}</p>
                  </div>
                )}
                {expandedId === a.id && <AnimalDetails a={a} />}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-surface-container-low">
                <tr>
                  {['Animal','Category','Price','Weight','Location','Stock','Status'].map(h => (
                    <th key={h} className="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-secondary whitespace-nowrap">{h}</th>
                  ))}
                  <th className="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-secondary sticky right-0 bg-surface-container-low whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container">
                {pagination.slice.map(a => (
                  <React.Fragment key={a.id}>
                  <tr ref={a.id === highlightAnimalId ? highlightRef : null}
                    className={`hover:bg-surface-container-low/50 transition-colors cursor-pointer ${
                      a.id === highlightAnimalId ? 'bg-primary/5 ring-2 ring-inset ring-primary/40' : ''
                    }`} onClick={() => toggleExpand(a.id)}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-outline text-[18px]">
                          {expandedId === a.id ? 'expand_less' : 'expand_more'}
                        </span>
                        {a.image_url
                          ? <img src={a.image_url} alt={a.name}
                              className="w-10 h-10 min-w-[40px] rounded-lg object-cover flex-shrink-0" />
                          : <div className="w-10 h-10 min-w-[40px] rounded-lg bg-surface-container flex items-center justify-center flex-shrink-0">
                              <span className="material-symbols-outlined text-outline text-lg">pets</span>
                            </div>
                        }
                        <span className="text-sm font-semibold">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-secondary">{a.category || '—'}</td>
                    <td className="px-5 py-3 text-sm font-bold text-primary">₹{Number(a.price_inr).toLocaleString('en-IN')}</td>
                    <td className="hidden lg:table-cell px-5 py-3 text-sm text-secondary">{a.weight_kg ? `${a.weight_kg} kg` : '—'}</td>
                    <td className="hidden lg:table-cell px-5 py-3 text-sm text-secondary">{a.location_city || '—'}</td>
                    <td className="px-5 py-3 text-xs text-secondary">
                      <span>{a.quantity}/{a.initial_quantity}</span>
                      <span className="ml-1 text-emerald-600 font-bold">
                        ({(a.initial_quantity || 0) - (a.quantity || 0)} sold)
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                        a.status === 'active'  ? 'bg-emerald-100 text-emerald-700' :
                        a.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        a.status === 'sold'    ? 'bg-blue-100 text-blue-700' :
                                                 'bg-gray-100 text-gray-600'
                      }`}>{a.status}</span>
                      {a.status === 'pending' && a.admin_note && (
                        <span
                          className="block mt-1 text-[10px] text-amber-700 font-semibold max-w-[160px] leading-snug"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          title={a.admin_note}>
                          ✏️ {a.admin_note}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 sticky right-0 bg-white shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center gap-1.5">
                        <button onClick={(e) => { e.stopPropagation(); toggleExpand(a.id); }}
                          className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-surface-container text-secondary hover:bg-surface-container-high transition-all flex items-center gap-1">
                          <span className="material-symbols-outlined text-[13px]">visibility</span>
                          Details
                        </button>
                        {(a.status === 'pending' || a.status === 'draft') && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); onEdit(a); }}
                              className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all flex items-center gap-1">
                              <span className="material-symbols-outlined text-[13px]">edit</span>
                              Edit
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
                              className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-all flex items-center gap-1">
                              <span className="material-symbols-outlined text-[13px]">delete</span>
                              Delete
                            </button>
                          </>
                        )}
                        {a.status === 'active' && a.quantity === a.initial_quantity && (
                          <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
                            className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-all flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px]">delete</span>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === a.id && (
                    <tr>
                      <td colSpan={8} className="p-0">
                        <AnimalDetails a={a} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {animals.length > 0 && (
        <div className="flex items-center justify-between px-4 sm:px-6 py-2 border-t border-surface-container-high">
          <div className="flex items-center gap-1">
            <span className="text-xs text-secondary mr-1">Per page</span>
            {[5, 10, 20].map(n => (
              <button key={n} onClick={() => { setPerPage(n); pagination.reset(); }}
                className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
                  ${perPage === n ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
                {n}
              </button>
            ))}
          </div>
          <p className="text-xs text-secondary">
            {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, animals.length)} of {animals.length}
          </p>
        </div>
      )}
      <PaginationBar page={pagination.page} totalPages={pagination.totalPages} setPage={pagination.setPage} />
    </div>
  );
}